/**
 * Resolve the API base URL.
 *
 * Normally this is just `REACT_APP_BACKEND_URL`, used verbatim. The one
 * exception is LAN development.
 *
 * `REACT_APP_BACKEND_URL` has to be a LAN IP (not `localhost`) for a phone on
 * the same Wi-Fi to reach the dev backend. But that IP is handed out by DHCP
 * and changes whenever the machine moves networks — after which every request
 * hits a dead host, gets no HTTP response at all, and the axios interceptor
 * flips the whole app to the offline screen. Diagnosing that as "no internet
 * connection" has cost real time more than once.
 *
 * When the configured host is a private LAN address, the page's OWN hostname
 * is already the correct one: in LAN dev the browser (or phone) loaded the
 * SPA from `http://<machine-ip>:3000`. So we take the host from
 * `window.location` and keep the configured port.
 *
 * Deliberately narrow — the rewrite applies ONLY when ALL of these hold:
 *   1. the configured URL's host is a private RFC-1918 / loopback address,
 *   2. the page itself is served from a private address (so a production
 *      build served from a public domain can never be rewritten),
 *   3. the two hosts actually differ,
 *   4. `REACT_APP_PIN_BACKEND_URL` is not set.
 *
 * Production bakes `https://dairyfreshapi.localappstore.in` — a public
 * domain, failing (1) — so it is never touched. Set
 * `REACT_APP_PIN_BACKEND_URL=true` to opt out entirely (e.g. pointing dev at
 * a specific LAN server that isn't the machine serving the web app).
 */

/** RFC-1918 private ranges, plus loopback. */
export function isPrivateHost(host) {
  if (!host) return false;
  const h = String(host).toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "0.0.0.0") return true;
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if (a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 127) return true;
  return false;
}

/**
 * @param {string} configured   REACT_APP_BACKEND_URL
 * @param {object} loc          window.location (or a stand-in)
 * @param {boolean} pinned      REACT_APP_PIN_BACKEND_URL
 * @returns {string} the base URL to use
 */
export function resolveBackendUrl(configured, loc, pinned = false) {
  if (!configured) return configured;
  if (pinned) return configured;
  if (!loc || !loc.hostname) return configured;

  let url;
  try {
    url = new URL(configured);
  } catch {
    // Not an absolute URL (e.g. a relative "/api" style value) — leave it be.
    return configured;
  }

  // (1) Only ever rewrite a LAN/loopback target...
  if (!isPrivateHost(url.hostname)) return configured;
  // (2) ...and only when the page itself is on a private address. A build
  // served from a public domain must never be repointed.
  if (!isPrivateHost(loc.hostname)) return configured;
  // (3) Already correct.
  if (url.hostname === loc.hostname) return configured;

  url.hostname = loc.hostname;
  // Port and protocol stay as configured — the API port is not the web port.
  return url.toString().replace(/\/$/, "");
}

const PINNED = String(process.env.REACT_APP_PIN_BACKEND_URL || "").toLowerCase() === "true";

export const BACKEND_URL = resolveBackendUrl(
  process.env.REACT_APP_BACKEND_URL,
  typeof window !== "undefined" ? window.location : null,
  PINNED,
);
